/**
 * Event detail index (`/me/events/:id`).
 *
 * The organizer's overview and the two actions that only exist here: running a
 * recommendation round and archiving the event. Editing, participants and the
 * round history all have their own child route, so nothing on this page is
 * repeated from them.
 */

import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

import { ErrorAlert } from '../../../components/layout/states'
import { Alert, AlertDescription } from '../../../components/ui/alert'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { Stat, StatGrid } from '../../../components/ui/primitives'
import { RelaxLimitsAction } from '../../../components/event/relax-limits-action'
import { useArchiveEvent, useRunRecommendation } from '../../../hooks/use-events'
import { useEventRoundPace } from '../../../hooks/use-round-pace'
import { ApiError } from '../../../lib/api'
import { currentLocale } from '../../../lib/i18n'
import { mutationErrorText } from '../../../lib/mutation-error'
import { activityLabelKey, formatLocalDateTime } from '../../../lib/format'
import { useEventDetailContext } from './layout'

export function EventDetailIndexPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { event } = useEventDetailContext()
  const runRecommendation = useRunRecommendation(event.id)
  // When this event's rounds are known to run long, the round is queued instead
  // of waited for, and the row it returns is polled by the round page.
  const pace = useEventRoundPace(event.id)
  const archiveEvent = useArchiveEvent(event.id)
  const [confirmingArchive, setConfirmingArchive] = useState(false)

  const locale = currentLocale()
  const runError = runRecommendation.error instanceof ApiError ? runRecommendation.error : null
  const runErrorText = mutationErrorText(runRecommendation.error, t)
  const archiveErrorText = mutationErrorText(archiveEvent.error, t)

  const archived = event.status === 'archived'
  const noParticipants = event.unitCount === 0
  const runBlocked = archived || noParticipants

  /**
   * Why the recommendation cannot run right now.
   *
   * The button stays visible and explains itself instead of failing with a
   * generic error the organizer cannot act on.
   */
  const runHint = archived
    ? t('events.archivedHint')
    : noParticipants
      ? t('events.triggerRecommendHint')
      : null

  // Both sides use the same format: an end date that silently switched to M/D
  // read like a different kind of value.
  const timeValue = event.eventDateEnd
    ? `${formatLocalDateTime(event.eventDateStart, locale)} → ${formatLocalDateTime(event.eventDateEnd, locale)}`
    : formatLocalDateTime(event.eventDateStart, locale)

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>{t('events.basic')}</CardTitle>
        </CardHeader>
        <CardContent>
          <StatGrid>
            <Stat label={t('eventNew.dateStartLabel')} value={timeValue} />
            <Stat
              label={t('events.participants')}
              value={event.peopleCount}
              hint={t('events.peopleAndUnits', {
                people: t('events.participantCount', { count: event.peopleCount }),
                units: t('events.unitCount', { count: event.unitCount }),
              })}
            />
            <Stat
              label={t('events.recommend')}
              value={event.roundCount}
              hint={t('events.roundCount', { count: event.roundCount })}
            />
            <Stat label={t('eventNew.weatherLabel')} value={t(`weather.${event.weatherCondition}`)} />
            <Stat
              label={t('events.activities')}
              value={
                <span className="flex flex-wrap gap-1">
                  {event.activities.map((activity) => (
                    <Badge key={activity} variant="outline">
                      {t(activityLabelKey(activity))}
                    </Badge>
                  ))}
                </span>
              }
            />
          </StatGrid>
        </CardContent>
      </Card>

      <Card>
        <CardContent
          className="flex flex-col gap-3 pt-4 sm:pt-5"
          aria-busy={runRecommendation.isPending}
        >
          <div className="flex flex-wrap items-center gap-2">
            <Button
              disabled={runRecommendation.isPending || runBlocked}
              onClick={() => {
                // A round can take a minute; the guard keeps a second click from
                // spending a second round while the first is still running.
                if (runRecommendation.isPending) {
                  return
                }
                runRecommendation.mutate(
                  { background: pace.isSlow },
                  {
                    onSuccess: (result) =>
                      navigate(`/me/events/${event.id}/recommend`, {
                        state: {
                          reused: result.reused,
                          pending: Boolean(result.pending),
                          round: result.round.round,
                        },
                      }),
                  },
                )
              }}
            >
              {runRecommendation.isPending
                ? t('overview.runningRecommend')
                : t('events.triggerRecommend')}
            </Button>
            <Button variant="outline" asChild>
              <Link to={`/me/events/${event.id}/recommend`}>{t('events.recommend')}</Link>
            </Button>
            {event.status === 'archived' ? null : confirmingArchive ? null : (
              <Button variant="ghost" onClick={() => setConfirmingArchive(true)}>
                {t('events.archive')}
              </Button>
            )}
          </div>
          {runHint ? <p className="text-muted-foreground text-xs">{runHint}</p> : null}
          {!runBlocked && pace.isSlow ? (
            <p className="text-muted-foreground text-xs">{t('overview.backgroundRunsHint')}</p>
          ) : null}

          {/* The organizer spends the same budget as a member, so the same three
              ceilings are stated here rather than only on the share page. */}
          <p className="text-muted-foreground text-xs">
            {event.recommendationBudget.remaining > 0
              ? t('overview.roundBudgetRemaining', {
                  remaining: event.recommendationBudget.remaining,
                  eventLimit: event.recommendationBudget.eventLimit,
                  memberLimit: event.recommendationBudget.memberLimit,
                })
              : t('overview.roundBudgetExhausted')}
          </p>

          {/* A round takes tens of seconds and the page cannot show progress, so it
              has to say that work is happening and that the page should stay open. */}
          {runRecommendation.isPending ? (
            <Alert variant="info">
              <AlertDescription>{t('overview.runningRecommendHint')}</AlertDescription>
            </Alert>
          ) : null}

          {runErrorText ? (
            <ErrorAlert message={runErrorText} />
          ) : null}

          <RelaxLimitsAction
            error={runError}
            isPending={runRecommendation.isPending}
            onRelax={(minutes) =>
              runRecommendation.mutate(
                { relaxedTravelMinutes: minutes, background: pace.isSlow },
                {
                  onSuccess: (result) =>
                    navigate(`/me/events/${event.id}/recommend`, {
                      state: {
                        reused: result.reused,
                        pending: Boolean(result.pending),
                        round: result.round.round,
                      },
                    }),
                },
              )
            }
          />

          {confirmingArchive ? (
            <Alert variant="warning">
              <AlertDescription>{t('events.archiveConfirm')}</AlertDescription>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={archiveEvent.isPending}
                  onClick={() =>
                    archiveEvent.mutate(undefined, {
                      onSuccess: () => setConfirmingArchive(false),
                    })
                  }
                >
                  {t('common.confirm')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setConfirmingArchive(false)}>
                  {t('common.cancel')}
                </Button>
              </div>
            </Alert>
          ) : null}

          {archiveErrorText ? (
            <ErrorAlert message={archiveErrorText} />
          ) : null}
        </CardContent>
      </Card>
    </div>
  )
}
